import { Invoice } from '@/api/queries/project/useGetProjectInvoices';
import {
  getRowGrantLine,
  isGrantRow,
  ProjectPaymentScheduleStage,
  ProjectPaymentScheduleStages,
} from '@/api/queries/project/useGetProjectPaymentSchedule';
import { Button, VerticalStepper } from '@payaca/ambrosia';

import { humanizeCurrency } from '@/helpers/currency';
import { formatPercentage } from '@payaca/helpers/financeHelper';
import { InvoicesPermissions } from '@payaca/permissions/invoices/invoices.permissions';
import { FC } from 'react';
import { InvoiceCard } from '../invoiceCard/InvoiceCard';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import { GrantPaidControls } from './GrantPaidControls';

const getStepperPropsFromInvoice = (
  invoice?: Invoice | null
): Omit<React.ComponentProps<typeof VerticalStepper.Step>, 'title'> => {
  if (!invoice) {
    return {
      colour: 'gray',
    };
  }

  if (
    invoice.outstandingValue.value > 0 &&
    invoice.sentAt &&
    invoice.dueAt &&
    new Date(invoice.dueAt) < new Date()
  ) {
    return {
      icon: 'hourglass-01',
      colour: 'red',
      isIncomplete: true,
    };
  }

  if (invoice.status === 'PAID') {
    return {
      icon: 'check',
      colour: 'green',
    };
  }

  if (invoice.status === 'PARTIALLY_PAID') {
    return {
      icon: 'file-04',
      colour: 'yellow',
      isIncomplete: true,
    };
  }

  if (invoice.status === 'SENT') {
    return {
      icon: 'mail-02.3',
      colour: 'blue',
      isIncomplete: true,
    };
  }

  return {
    icon: 'file-04',
    colour: 'gray',
  };
};

/**
 * A grant stage is settled by "Mark as paid" (which creates its grant invoice
 * and payment together) rather than "Create next invoice". Once paid it shows
 * its grant invoice card like any other stage.
 */
const GrantStageContent: FC<{
  stage: ProjectPaymentScheduleStage;
  projectPublicId: string;
  onMarkGrantAsPaidSuccess?: () => void;
  onRecordPaymentSuccess?: () => void;
}> = ({
  stage,
  projectPublicId,
  onMarkGrantAsPaidSuccess,
  onRecordPaymentSuccess,
}) => {
  const grantLine = getRowGrantLine(stage);
  return (
    <div className="space-y-2">
      {stage.invoice ? (
        <>
          <InvoiceCard
            invoice={stage.invoice}
            onRecordPaymentSuccess={onRecordPaymentSuccess}
          />
          {grantLine && (
            <GrantPaidControls
              grantLineId={grantLine.id}
              projectPublicId={projectPublicId}
              isPaid
              onSuccess={onMarkGrantAsPaidSuccess}
            />
          )}
        </>
      ) : (
        grantLine && (
          <GrantPaidControls
            grantLineId={grantLine.id}
            projectPublicId={projectPublicId}
            isPaid={false}
            onSuccess={onMarkGrantAsPaidSuccess}
          />
        )
      )}
    </div>
  );
};

export const ProjectPaymentScheduleStepper: FC<{
  stages: ProjectPaymentScheduleStages;
  projectPublicId: string;
  onRecordPaymentSuccess?: () => void;
  onMarkGrantAsPaidSuccess?: () => void;
  requestCreateNextInvoice: () => void;
}> = ({
  requestCreateNextInvoice,
  stages,
  projectPublicId,
  onRecordPaymentSuccess,
  onMarkGrantAsPaidSuccess,
}) => {
  if (stages.length === 0) {
    return null;
  }

  // Grant stages are settled via "Mark as paid", never "Create next invoice",
  // so they are excluded from the next-invoice search.
  const createNextInvoiceIndex = stages.findIndex(
    (stage) => !stage.invoice && !isGrantRow(stage)
  );

  return (
    <VerticalStepper>
      {stages.map((row, index) => {
        const isGrant = isGrantRow(row);
        const isPaidGrant = isGrant && !!row.invoice;

        let title: string;
        if (isGrant) {
          const grantName =
            getRowGrantLine(row)?.name ?? row.stage?.description ?? 'Grant';
          title = row.stage?.value.value
            ? `Grant - ${grantName} - ${humanizeCurrency(row.stage.value)}`
            : `Grant - ${grantName}`;
        } else if (row.stage && row.stage.__typename !== 'DerivedGrantStage') {
          const { description, shareOfTotal, value } = row.stage;
          title =
            shareOfTotal == null
              ? `${description} - ${humanizeCurrency(value)}`
              : `${description} - ${formatPercentage(shareOfTotal)}%`;
        } else {
          title = `Stage ${index + 1}`;
        }

        return (
          <VerticalStepper.Step
            {...(isGrant
              ? {
                  icon: isPaidGrant ? 'check' : 'coins-hand.3',
                  colour: isPaidGrant ? 'green' : 'gray',
                }
              : getStepperPropsFromInvoice(row.invoice))}
            key={index}
            title={title}
          >
            {isGrant ? (
              <GrantStageContent
                stage={row}
                projectPublicId={projectPublicId}
                onMarkGrantAsPaidSuccess={onMarkGrantAsPaidSuccess}
                onRecordPaymentSuccess={onRecordPaymentSuccess}
              />
            ) : (
              <>
                {row.invoice && (
                  <InvoiceCard
                    invoice={row.invoice}
                    onRecordPaymentSuccess={onRecordPaymentSuccess}
                  />
                )}
                {createNextInvoiceIndex === index && (
                  <PermissionGuard
                    renderIfHasPermissions={[
                      InvoicesPermissions.CREATE_INVOICE,
                    ]}
                  >
                    <Button
                      size="sm"
                      variant="tertiary"
                      onClick={requestCreateNextInvoice}
                    >
                      Create next invoice
                    </Button>
                  </PermissionGuard>
                )}
              </>
            )}
          </VerticalStepper.Step>
        );
      })}
    </VerticalStepper>
  );
};
